import { Head, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { EnquiryStorageNotice } from "@/components/EnquiryStorageNotice"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import type { CallbackRequest, PageProps } from "@/types/inertia"

type Props = PageProps & {
  requests: CallbackRequest[]
  storing: boolean
}

const STATUS_TONES: Record<string, "accent" | "neutral" | "signal"> = {
  pending: "signal",
  called: "accent",
  closed: "neutral",
}

function telHref(phone: string) {
  return `tel:${phone.replace(/\s/g, "")}`
}

function formatRequestedAt(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    timeZone: "Europe/London",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export default function CallbackRequestsIndex() {
  const { props } = usePage<Props>()
  const { requests, storing } = props
  const flash = props.flash

  function markStatus(request: CallbackRequest, status: "called" | "closed") {
    router.patch(`/admin/callback-requests/${request.id}`, {
      callback_request: { status },
    })
  }

  return (
    <AdminShell>
      <Head title="Callback Requests">
        <meta name="description" content="People who couldn't reach Gisela and asked for a callback." />
        <meta property="og:title" content="Callback Requests" />
        <meta property="og:description" content="People who couldn't reach Gisela and asked for a callback." />
      </Head>

      <PageHeader
        title="Callback Requests"
        description="People who rang and couldn't get through — call them back, then mark the request as called."
      />

      {!storing && <EnquiryStorageNotice what="Callback requests" />}

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}

      <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead className="border-b border-hairline bg-surface">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Requested (UK)</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Name</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Phone</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Note</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Status</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {requests.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-muted">
                  No callback requests.
                </td>
              </tr>
            )}
            {requests.map((request) => (
              <tr key={request.id} className="bg-white transition-colors hover:bg-surface/30">
                <td className="whitespace-nowrap px-4 py-3 tabular-nums text-ink-body">
                  {formatRequestedAt(request.created_at)}
                </td>
                <td className="px-4 py-3">
                  <p className="text-ink-body">{request.name}</p>
                  {request.email && <p className="text-xs text-ink-muted">{request.email}</p>}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <a href={telHref(request.phone)}>{request.phone}</a>
                  <p className="text-xs text-ink-muted">
                    {request.contact_method === "whatsapp" ? "WhatsApp" : "Mobile call"}
                  </p>
                </td>
                <td className="hidden max-w-xs px-4 py-3 text-ink-muted lg:table-cell">
                  {request.note && (
                    <p className="truncate" title={request.note}>
                      {request.note}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={STATUS_TONES[request.status] ?? "neutral"}>
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {request.status === "pending" && (
                      <Button variant="secondary" size="sm" onClick={() => markStatus(request, "called")}>
                        Mark called
                      </Button>
                    )}
                    {request.status !== "closed" && (
                      <Button variant="secondary" size="sm" onClick={() => markStatus(request, "closed")}>
                        Close
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminShell>
  )
}
